import { IDictationItem } from 'common/modules/ReactShared';
import { AnimationControls } from 'framer-motion';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  DEFAULT_TASK_TIMEOUT,
  TASK_MINIMAL_TIMEOUT,
  TASK_TIMEOUT_MULTIPLIER
} from '../constants';
import {
  buildTimeoutBlockAnimation,
  HIDDEN_TIMEOUT_BLOCK_POSITION
} from '../timeoutBlockAnimation';

export function useItemTime({
  item,
  timeoutBlockControls,
  onItemTimeIsUp,
  countdownEnabled = true
}: {
  item: IDictationItem;
  timeoutBlockControls: AnimationControls;
  onItemTimeIsUp: () => void;
  /** False when the student has switched the countdown off: no time limit, no draining background. */
  countdownEnabled?: boolean;
}): {
  initItemDurationAnimation: () => void;
  handleStopAudio: () => void;
  stopTimer: () => void;
} {
  const itemDurationRef = useRef<number>(DEFAULT_TASK_TIMEOUT);
  const taskTimeout = useRef<number | null>(null);
  const startTime = useRef<number | null>(null);
  const [audioHasPlayed, setAudioHasPlayed] = useState(false);

  const startItemTimer = useCallback(() => {
    if (!countdownEnabled) return;

    if (!taskTimeout.current) {
      taskTimeout.current = window.setTimeout(() => {
        onItemTimeIsUp();
      }, itemDurationRef.current);

      startTime.current = new Date().getTime();
    }
  }, [onItemTimeIsUp, countdownEnabled]);

  const stopItemTimer = useCallback(() => {
    taskTimeout.current && clearTimeout(taskTimeout.current);
    taskTimeout.current = null;
    startTime.current = null;
  }, []);

  useEffect(() => {
    return () => {
      stopItemTimer();
    };
  }, [stopItemTimer]);

  useEffect(() => {
    if (!countdownEnabled) return undefined;

    function onDocumentVisibilityChange() {
      // Nothing to pause or resume until the countdown has started, e.g. when the app is
      // backgrounded while the audio is still playing.
      if (startTime.current === null) return;

      // Clamped: a tab left in the background longer than the item lasts resumes at
      // "time is up" rather than with a negative duration.
      const currentProgress = Math.min(
        new Date().getTime() - startTime.current,
        itemDurationRef.current
      );
      const currentProgressPercent = currentProgress / itemDurationRef.current;

      if (document.hidden) {
        timeoutBlockControls.stop();
      } else {
        timeoutBlockControls.start(
          buildTimeoutBlockAnimation(
            currentProgressPercent,
            (itemDurationRef.current - currentProgress) / 1000
          )
        );
      }
    }

    document.addEventListener('visibilitychange', onDocumentVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', onDocumentVisibilityChange);
    };
  }, [timeoutBlockControls, countdownEnabled]);

  useEffect(() => {
    if (item.duration) {
      itemDurationRef.current = item.duration * 1000;
    } else {
      const audio = new Audio();

      audio.addEventListener('loadedmetadata', () => {
        itemDurationRef.current =
          audio.duration * 1000 * TASK_TIMEOUT_MULTIPLIER + TASK_MINIMAL_TIMEOUT;
      });

      audio.preload = 'metadata';
      audio.src = item.audioURL;
    }
  }, [item]);

  const startItemDurationTimerAnimation = useCallback(() => {
    if (!countdownEnabled) return;

    timeoutBlockControls.start(
      buildTimeoutBlockAnimation(0, itemDurationRef.current / 1000)
    );
  }, [timeoutBlockControls, countdownEnabled]);

  const initItemDurationAnimation = useCallback(() => {
    timeoutBlockControls.set(HIDDEN_TIMEOUT_BLOCK_POSITION);
  }, [timeoutBlockControls]);

  const handleStopAudio = useCallback(() => {
    setAudioHasPlayed(true);
  }, []);

  // Run the countdown once the audio has played. Re-runs when the student switches the
  // countdown back on mid-item, so timer and background always start together.
  useEffect(() => {
    if (audioHasPlayed && countdownEnabled && !taskTimeout.current) {
      startItemTimer();
      startItemDurationTimerAnimation();
    }
  }, [audioHasPlayed, countdownEnabled, startItemTimer, startItemDurationTimerAnimation]);

  // Switching the countdown off mid-item cancels the in-flight timer and hides the
  // draining background, so the toggle in the lesson settings modal applies at once.
  useEffect(() => {
    if (countdownEnabled) return;

    stopItemTimer();
    timeoutBlockControls.stop();
    timeoutBlockControls.set(HIDDEN_TIMEOUT_BLOCK_POSITION);
  }, [countdownEnabled, stopItemTimer, timeoutBlockControls]);

  return {
    initItemDurationAnimation,
    handleStopAudio,
    stopTimer: stopItemTimer
  };
}
